/**
 * Meta (Facebook) Pixel.
 *
 * The pixel is privacy-gated: `initMetaPixel` is only ever called once the user
 * has accepted cookies (see `MetaPixel` component + the `cconsent` opt-in flow
 * in `@/ampli`). Until then `window.fbq` is undefined and `trackMetaPixelLead`
 * is a no-op, so nothing is sent to Meta.
 */

const metaPixelId = '2417547959036634';
const fbeventsSrc = 'https://connect.facebook.net/en_US/fbevents.js';

interface Fbq {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[][];
  push?: Fbq;
  loaded?: boolean;
  version?: string;
}

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

let isInitialized = false;

/**
 * Replicates Meta's official bootstrap snippet: stubs `window.fbq` with a queue
 * and asynchronously loads `fbevents.js`. Idempotent.
 */
function loadFbevents(): void {
  if (window.fbq) {
    return;
  }

  const fbq: Fbq = (...args: unknown[]): void => {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
      return;
    }
    fbq.queue?.push(args);
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];

  window.fbq = fbq;
  window._fbq ??= fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = fbeventsSrc;

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
}

/**
 * Initialises the Meta Pixel and fires the initial `PageView`. MUST only be
 * called after the user has accepted cookies. Safe to call multiple times.
 */
export function initMetaPixel(): void {
  if (typeof window === 'undefined' || isInitialized) {
    return;
  }
  isInitialized = true;

  loadFbevents();
  window.fbq?.('init', metaPixelId);
  window.fbq?.('track', 'PageView');
}

/**
 * Tracks a waitlist sign-up as the standard Meta `Lead` conversion event.
 * No-op until the pixel has been initialised (i.e. consent granted), keeping
 * tracking best-effort and consent-safe.
 */
export function trackMetaPixelLead(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.fbq?.('track', 'Lead');
}

/**
 * Tracks a successful account signup separately from waitlist leads.
 */
export function trackMetaPixelSignup(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.fbq?.('track', 'CompleteRegistration', {
    content_name: 'Budgy account',
  });
}
